import { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";

/**
 * useHls — Shared hook for HLS (.m3u8) playback via hls.js
 *
 * Handles:
 *   - HLS stream loading on Chrome/Firefox/Edge via hls.js
 *   - Native HLS fallback for Safari
 *   - Quality level detection + manual switching
 *   - Audio track detection + switching
 *   - Subtitle track detection + switching
 *   - Cleanup on unmount or URL change
 *
 * Usage:
 *   const { qualities, audioTracks, subtitleTracks, setQuality, setAudioTrack, setSubtitleTrack }
 *     = useHls(videoRef, playbackUrl);
 *
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 * @param {string|null} url — HLS stream URL (.m3u8) or direct video URL
 * @param {Object} [options]
 * @param {boolean} [options.autoPlay=true]
 * @returns {Object}
 */
const useHls = (videoRef, url, options = {}) => {
  const { autoPlay = true } = options;
  const hlsRef = useRef(null);

  const [qualities, setQualities] = useState([]);       // [{ index, height, bitrate, label }]
  const [currentQuality, setCurrentQuality] = useState(-1); // -1 = auto
  const [audioTracks, setAudioTracks] = useState([]);    // [{ index, name, lang }]
  const [currentAudioTrack, setCurrentAudioTrack] = useState(0);
  const [subtitleTracks, setSubtitleTracks] = useState([]); // [{ index, name, lang }]
  const [currentSubtitleTrack, setCurrentSubtitleTrack] = useState(-1); // -1 = off
  const [isHlsActive, setIsHlsActive] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    const isM3u8 = url.includes(".m3u8");

    // HLS.js path — Chrome, Firefox, Edge
    if (isM3u8 && Hls.isSupported()) {
      console.log("[useHls] Using hls.js for:", url.substring(0, 80) + "...");

      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        // Start playback at the highest quality instead of ramping up from the
        // lowest. -1 here means "let us set it explicitly in MANIFEST_PARSED".
        startLevel: -1,
        // Assume a fast connection so ABR doesn't pin the first segments to a
        // low rendition while it measures real bandwidth.
        abrEwmaDefaultEstimate: 5_000_000, // 5 Mbps
      });

      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        console.log("[useHls] Manifest parsed:", data.levels.length, "quality levels");

        // Quality levels
        const q = data.levels.map((level, i) => ({
          index: i,
          height: level.height,
          bitrate: level.bitrate,
          label: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)}kbps`,
        }));
        setQualities(q);

        // Force the initial segment to load at the highest available quality.
        // hls.js sorts levels ascending by bitrate, so the last index is the
        // highest rendition. currentLevel stays auto (-1), so ABR can still
        // step down later if bandwidth genuinely drops.
        if (data.levels.length > 0) {
          const highest = data.levels.length - 1;
          console.log("[useHls] Setting start level to highest quality:", highest, q[highest]?.label);
          hls.startLevel = highest;
        }

        // Audio tracks
        const audio = hls.audioTracks.map((track, i) => ({
          index: i,
          name: track.name || `Audio ${i + 1}`,
          lang: track.lang || "",
        }));
        setAudioTracks(audio);
        console.log("[useHls] Audio tracks:", audio.length);

        // Subtitle tracks
        const subs = hls.subtitleTracks.map((track, i) => ({
          index: i,
          name: track.name || `Subtitle ${i + 1}`,
          lang: track.lang || "",
        }));
        setSubtitleTracks(subs);
        console.log("[useHls] Subtitle tracks:", subs.length);

        setIsHlsActive(true);

        if (autoPlay) {
          video.play().catch((e) => {
            console.warn("[useHls] Autoplay blocked:", e.message);
          });
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error("[useHls] Fatal error:", data.type, data.details);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            console.log("[useHls] Network error, attempting recovery...");
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            console.log("[useHls] Media error, attempting recovery...");
            hls.recoverMediaError();
          } else {
            hls.destroy();
          }
        }
      });

      return () => {
        console.log("[useHls] Destroying hls.js instance");
        hls.destroy();
        hlsRef.current = null;
        setIsHlsActive(false);
        setQualities([]);
        setAudioTracks([]);
        setSubtitleTracks([]);
      };
    }

    // Native HLS path — Safari
    if (isM3u8 && video.canPlayType("application/vnd.apple.mpegurl")) {
      console.log("[useHls] Using native HLS (Safari) for:", url.substring(0, 80) + "...");
      video.src = url;
      setIsHlsActive(false);
      if (autoPlay) {
        video.play().catch((e) => {
          console.warn("[useHls] Autoplay blocked:", e.message);
        });
      }
      return;
    }

    // Non-HLS (direct MP4 etc) — just set src
    if (!isM3u8) {
      console.log("[useHls] Non-HLS source, setting directly:", url.substring(0, 80) + "...");
      video.src = url;
      setIsHlsActive(false);
      if (autoPlay) {
        video.play().catch((e) => {
          console.warn("[useHls] Autoplay blocked:", e.message);
        });
      }
    }
  }, [url, videoRef, autoPlay]);

  // Quality switch
  const setQuality = useCallback((levelIndex) => {
    const hls = hlsRef.current;
    if (!hls) return;
    console.log("[useHls] Switching quality to level:", levelIndex, levelIndex === -1 ? "(auto)" : "");
    hls.currentLevel = levelIndex;
    setCurrentQuality(levelIndex);
  }, []);

  // Audio track switch
  const setAudioTrackIndex = useCallback((trackIndex) => {
    const hls = hlsRef.current;
    if (!hls) return;
    console.log("[useHls] Switching audio track to:", trackIndex);
    hls.audioTrack = trackIndex;
    setCurrentAudioTrack(trackIndex);
  }, []);

  // Subtitle track switch
  const setSubtitleTrackIndex = useCallback((trackIndex) => {
    const hls = hlsRef.current;
    if (!hls) return;
    console.log("[useHls] Switching subtitle track to:", trackIndex, trackIndex === -1 ? "(off)" : "");
    hls.subtitleTrack = trackIndex;
    setCurrentSubtitleTrack(trackIndex);
  }, []);

  return {
    qualities,
    currentQuality,
    setQuality,
    audioTracks,
    currentAudioTrack,
    setAudioTrack: setAudioTrackIndex,
    subtitleTracks,
    currentSubtitleTrack,
    setSubtitleTrack: setSubtitleTrackIndex,
    isHlsActive,
    hlsRef,
  };
};

export default useHls;
